import type { Bot } from "@rakazo/contracts";
import { ALLSPICE_CREW, type CrewMember } from "./allspice-crew";
import { rpc } from "./rpc";

/** "SEO Expert" from "SEO Expert: rankings, keywords, …". */
export function crewTitle(member: CrewMember): string {
  return member.description.split(":")[0] ?? member.name;
}

/** What the role does, without the title. */
export function crewBlurb(member: CrewMember): string {
  const colon = member.description.indexOf(":");
  return colon >= 0 ? member.description.slice(colon + 1).trim() : member.description;
}

/** Default team members not currently on the team: never hired, or deleted
 * earlier (a deleted teammate leaves the active list, so it is offered again).
 * A custom bot that reuses a member's name counts as that seat being filled. */
export function missingCrew(bots: Bot[]): CrewMember[] {
  const keys = new Set(bots.map((bot) => bot.spawnKey));
  const names = new Set(bots.map((bot) => bot.name.trim().toLowerCase()));
  return ALLSPICE_CREW.filter(
    (member) => !keys.has(member.spawnKey) && !names.has(member.name.toLowerCase()),
  );
}

/** Hire one default team member. The spawnKey keeps concurrent hires idempotent;
 * when a deleted teammate still holds the key, fall back to a plain create. */
export async function hireCrewMember(member: CrewMember): Promise<{ id: string }> {
  const input = {
    name: member.name,
    description: member.description,
    instructions: member.instructions,
    color: member.color,
    notifyOnFinish: true,
  };
  try {
    const created = await rpc.bots.create({ ...input, spawnKey: member.spawnKey });
    return { id: created.id };
  } catch (error) {
    const existing = await rpc.bots.list();
    const winner = existing.find((bot) => bot.spawnKey === member.spawnKey);
    if (winner) return { id: winner.id };
    // The key is held by an archived teammate; a re-hire is a fresh bot.
    const created = await rpc.bots.create(input).catch(() => {
      throw error;
    });
    return { id: created.id };
  }
}
